import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import Email from "./Email";
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { ValidationUtil } from '../../util/ValidationUtil';
import { useMemo, useCallback } from 'react';

interface EmailControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: string): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const EmailControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: EmailControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Email, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Email, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Memoize processed errors
    const processedErrors = useMemo(() =>
            ValidationUtil.isEmptyValue(value) ? "" : errors,
        [errors, value]
    );

    // Memoize the change handler
    const handleEmailChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(path, ev.target.value);
    }, [handleChange, path]);

    // Memoize the Email component props
    const emailProps = useMemo(() => ({
        variant: 'outlined' as const,
        error: processedErrors,
        value: value,
        required: required,
        label: uischema.label,
        description: schema.description,
        onChange: handleEmailChange,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        processedErrors,
        value,
        required,
        uischema.label,
        schema.description,
        handleEmailChange,
        enabled,
        visible,
        appValidated
    ]);

    return <Email {...emailProps} />;
};

// Export with memoization
export default React.memo(withJsonFormsControlProps(EmailControl));
